import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Target, Briefcase, Building2 } from "lucide-react";
import { formatApiError } from "../lib/api";
import useDocumentTitle from "../hooks/useDocumentTitle";
import PasswordInput from "../components/PasswordInput";
import PasswordStrengthIndicator, { isPasswordStrong } from "../components/PasswordStrengthIndicator";

export default function Signup() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "employer" ? "employer" : "job_seeker");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useDocumentTitle("Sign up");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong(password)) {
      toast.error("Password must be 8+ characters with uppercase, lowercase, and a special character.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await register({ email, password, name, role });
      if (result?.verification_email_sent === false) {
        toast.error(
          "Account created — but we couldn't send the verification email right now. Open the notification bell after signing in to Resend, or email support@qatestology.com.",
          { duration: 10000 }
        );
      } else {
        toast.success(`Account created — check ${email} (and your spam folder) for the verification link.`, { duration: 8000 });
      }
      const returnTo = params.get("return_to");
      if (returnTo && returnTo.startsWith("/")) {
        navigate(returnTo);
      } else {
        navigate(role === "employer" ? "/employer/profile" : "/profile");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sign up failed");
    } finally {
      setSubmitting(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signupWithGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative bg-zinc-950 text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(0,47,167,0.4),transparent_60%)]" />
        <Link to="/" className="relative flex items-center gap-2">
          <span className="h-8 w-8 rounded-md bg-[#002FA7] grid place-items-center">
            <Target className="h-4 w-4" strokeWidth={2.5} />
          </span>
          <span className="font-display text-lg font-bold">QaTestology</span>
        </Link>
        <div className="relative">
          <div className="font-mono text-xs uppercase tracking-widest text-emerald-400 mb-3">// join the platform</div>
          <h2 className="font-display text-4xl font-black tracking-tighter leading-tight">
            Built for QA.<br />Trusted by hiring teams.
          </h2>
          <ul className="mt-6 space-y-2 text-zinc-400 text-sm">
            <li>• AI Skill Gap Analyzer — know what to learn next.</li>
            <li>• Smart candidate matching for employers.</li>
            <li>• Free to start — no credit card.</li>
          </ul>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <span className="h-8 w-8 rounded-md bg-[#002FA7] text-white grid place-items-center">
              <Target className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <span className="font-display text-lg font-bold">QaTestology</span>
          </Link>

          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-950">Create your account</h1>
          <p className="mt-2 text-sm text-zinc-500">It takes less than a minute.</p>

          {/* Role toggle */}
          <div className="mt-6 grid grid-cols-2 gap-2 p-1 bg-zinc-100 rounded-md">
            <button
              type="button"
              onClick={() => setRole("job_seeker")}
              data-testid="role-seeker-btn"
              className={`flex items-center justify-center gap-2 py-2 rounded text-sm font-medium transition-colors ${
                role === "job_seeker" ? "bg-white shadow-sm text-zinc-950" : "text-zinc-600 hover:text-zinc-950"
              }`}
            >
              <Briefcase className="h-4 w-4" /> Job seeker
            </button>
            <button
              type="button"
              onClick={() => setRole("employer")}
              data-testid="role-employer-btn"
              className={`flex items-center justify-center gap-2 py-2 rounded text-sm font-medium transition-colors ${
                role === "employer" ? "bg-white shadow-sm text-zinc-950" : "text-zinc-600 hover:text-zinc-950"
              }`}
            >
              <Building2 className="h-4 w-4" /> Employer
            </button>
          </div>

          <Button
            type="button"
            variant="outline"
            className="mt-6 w-full h-11 border-zinc-300"
            onClick={signupWithGoogle}
            data-testid="signup-google-btn"
          >
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-zinc-200" />
            <span className="text-xs uppercase tracking-widest text-zinc-400 font-mono">or</span>
            <div className="h-px flex-1 bg-zinc-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">{role === "employer" ? "Your name" : "Full name"}</Label>
              <Input id="name" required value={name} onChange={(e)=>setName(e.target.value)} data-testid="signup-name-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="signup-email-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" required minLength={8} value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="signup-password-input" showTestId="signup-password-toggle" className="mt-1.5" />
              <PasswordStrengthIndicator password={password} />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="signup-submit-btn"
              className="w-full h-11 bg-[#002FA7] hover:bg-[#00227A] text-white"
            >
              {submitting ? "Creating..." : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-zinc-600">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-[#002FA7] hover:text-[#00227A]" data-testid="link-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
